import { computed, effect, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { atLeastOneFieldFilled } from '@shared/utils/at-least-one-field-filled.validator';

import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { CartographieStore } from '../../../cartographie/store/cartographie.store';
import { Certification } from '../../../croisement-référentiel/models/certification';
import { CertificationStore } from '../../../croisement-référentiel/store/certification.store';
import { InterrelationStore } from '../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { FamilleLivrables } from '../../../référentiel-métier/référentiel-métier-périmètre/perimetre-metier/models/famille-livrables.model';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { ÉtapeInterrelation } from '../..//models/étape-interrelation.model';
import { FormControlNames, FormGroupNames } from '../../models/form-control-names.model';
import { ÉTAT_VALIDATION_ÉTAPE } from '../../models/état-validation-étape.enum';
import { EtapeStore } from '../../parcours-etape/store/etape.store';
import { GroupeÉtapesÀLaCarteStore } from '../../store/groupe-étapes-à-la-carte/groupe-étapes-à-la-carte.store';

export interface InfosCertification {
  aptitudes: Set<string>;
  interrelations: Set<string>;
  famillesLivrables: Set<string>;
  connaissances: Set<string>;
  documentations: Set<string>;
  materiels: Set<string>;
  habiletes: Set<string>;
}

@Injectable({
  providedIn: 'root',
})
export class EtapeDetailFormService {
  readonly #fb = inject(FormBuilder);
  readonly #etapeStore = inject(EtapeStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #certificationStore = inject(CertificationStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #groupeEtapeStore = inject(GroupeÉtapesÀLaCarteStore);
  readonly #canEditService = inject(CanEditService);
  readonly #cartographieStore = inject(CartographieStore);

  #etapeSelectionnee = computed(() => this.#etapeStore.selectedEntity());
  readonly referentiel = linkedSignal<Référentiel | null>(() => null);
  readonly cartographie = computed(() => this.#cartographieStore.selectedEntity());

  readonly certificationAttachee = linkedSignal(() => this.#etapeSelectionnee()?.certification);
  readonly aptitudesCertif = linkedSignal(
    () =>
      this.certificationAttachee()
        ?.blocsCompetences.flatMap((x) => x.competences)
        .flatMap((x) => x.aptitudes ?? [])
        .filter((apt1, i, list) => list.findIndex((apt2) => apt2.id === apt1.id) === i)
        .map((aptitude) => ({
          id: aptitude.id,
          idPhase: aptitude.idPhase,
          nom: aptitude.nom,
          productions: aptitude.productions,
          interrelations: this.#interrelationStore.parPhase(aptitude.idPhase),
        })) ?? [],
  );

  form: FormGroup = this.#fb.group({
    [FormGroupNames.GENERAL]: this.#fb.group({
      [FormControlNames.NOM]: [null, [Validators.required]],
      [FormControlNames.CODE]: [
        null,
        [Validators.pattern('[A-Z]{3}(0|[3-8])[A-Z]{3}\\d+\\.\\d{1,2}|[A-Z]{3}(0|[3-8])[A-Z]{3}\\d{3}')],
      ],
      [FormControlNames.ANCIEN_CODE]: [null],
      [FormControlNames.A_CHANGEMENT_VILLE]: [false],
      [FormControlNames.A_CURSUS_INTERNATIONAL]: [false],
      [FormControlNames.EST_OUVERTE]: [false],
      [FormControlNames.EST_À_LA_CARTE]: [false],
      [FormControlNames.GROUPES]: [[]],
    }),
    [FormGroupNames.CONTRAT]: this.#fb.group(
      {
        [FormControlNames.TYPES_CONTRAT]: [[], [Validators.minLength(1)]],
        [FormControlNames.AUTRE_TYPE]: [null],
        [FormControlNames.MODALITE]: [null],
        [FormControlNames.DUREE]: [null, [Validators.required]],
        [FormControlNames.REPARTITIONS_ANNEE]: this.#fb.array([]),
        [FormControlNames.TYPE]: [null],
        [FormControlNames.EST_CERTIFIANTE]: [false],
      },
      { validators: atLeastOneFieldFilled(FormControlNames.TYPES_CONTRAT, FormControlNames.AUTRE_TYPE) },
    ),
    [FormGroupNames.CERTIFICATIONS]: this.#fb.group({
      [FormControlNames.CERTIFICATION]: [null],
    }),
    [FormGroupNames.FORMATIONS_REGLEMENTAIRES]: this.#fb.group({
      [FormControlNames.NOM_FORMATIONS_REGLEMENTAIRES]: [[]],
    }),
    [FormGroupNames.OBJECTIFS]: this.#fb.group({
      [FormControlNames.APTITUDES]: this.#fb.array([]),
      [FormControlNames.INTERRELATIONS]: [[]],
      [FormControlNames.PRODUCTIONS]: [[]],
    }),
    [FormGroupNames.LIVRABLES]: this.#fb.group({
      [FormControlNames.FAMILLES_LIVRABLES]: this.#fb.array([]),
    }),
  });

  readonly defaultTypesContrat = ['professionnalisation', 'apprentissage'];

  readonly showErrors = signal<boolean | undefined>(false);

  constructor() {
    effect(() => {
      const étape = this.#etapeSelectionnee();
      const showErrors = this.showErrors();

      if (étape) {
        this.famillesLivrables.clear({ emitEvent: false });
        this.aptitudes.clear({ emitEvent: false });
        this.repartitionsAnnee.clear({ emitEvent: false });
        this.form.patchValue(
          {
            [FormGroupNames.GENERAL]: {
              [FormControlNames.NOM]: étape.nom ?? null,
              [FormControlNames.CODE]: étape.codeEtape ?? null,
              [FormControlNames.ANCIEN_CODE]: étape.ancienCodeEtape ?? null,
              [FormControlNames.A_CHANGEMENT_VILLE]: étape.aChangementVille ?? false,
              [FormControlNames.A_CURSUS_INTERNATIONAL]: étape.aCursusInternational ?? false,
              [FormControlNames.EST_OUVERTE]: étape.estOuverte,
              [FormControlNames.EST_À_LA_CARTE]: étape.idsGroupesEtapesALaCarte
                ? étape.idsGroupesEtapesALaCarte.length > 0
                : false,
              [FormControlNames.GROUPES]: étape.idsGroupesEtapesALaCarte ?? [],
            },
            [FormGroupNames.CONTRAT]: {
              [FormControlNames.TYPES_CONTRAT]: étape.typesContrat ?? [],
              [FormControlNames.AUTRE_TYPE]:
                étape.typesContrat?.filter((x) => !this.defaultTypesContrat.includes(x))[0] ?? null,
              [FormControlNames.MODALITE]: étape.modalite ? étape.modalite.id : null,
              [FormControlNames.DUREE]: étape.duree ?? null,
              [FormControlNames.REPARTITIONS_ANNEE]: [],
              [FormControlNames.EST_CERTIFIANTE]: (étape.estCertifiante || étape.certification != undefined) ?? false,
              [FormControlNames.TYPE]: étape.type ?? null,
            },
            [FormGroupNames.CERTIFICATIONS]: {
              [FormControlNames.CERTIFICATION]: étape.certification ?? null,
            },
            [FormGroupNames.FORMATIONS_REGLEMENTAIRES]: {
              [FormControlNames.NOM_FORMATIONS_REGLEMENTAIRES]: [],
            },
            [FormGroupNames.OBJECTIFS]: {
              [FormControlNames.APTITUDES]: [],
              [FormControlNames.PRODUCTIONS]: étape.productions ?? [],
              [FormControlNames.INTERRELATIONS]:
                étape.interrelations.map((x) => ({ id: `${x.idAptitude}-${x.idInterrelation}` })) ?? [],
            },
            [FormGroupNames.LIVRABLES]: {
              [FormControlNames.FAMILLES_LIVRABLES]: [],
            },
          },
          { emitEvent: false },
        );

        étape.famillesLivrables.forEach((fdl) =>
          this.famillesLivrables.push(this.#fb.control(fdl), { emitEvent: false }),
        );

        étape.aptitudes.forEach((aptitude) => this.aptitudes.push(this.#fb.control(aptitude), { emitEvent: false }));

        if (étape.duree) {
          étape.repartitionsAnnee?.forEach((repartitionAnnee) =>
            this.repartitionsAnnee.push(
              this.#fb.nonNullable.group({
                nombreMois: this.#fb.control(repartitionAnnee?.nombreMois, [Validators.required, Validators.min(1)]),
                nombreSemainesCFA: this.#fb.control(repartitionAnnee?.nombreSemainesCFA, [Validators.min(0)]),
                nombreSemainesEntreprise: this.#fb.control(repartitionAnnee?.nombreSemainesEntreprise, [
                  Validators.min(0),
                ]),
                numeroAnnee: repartitionAnnee.numeroAnnee as 1 | 2 | 3,
              }),
              { emitEvent: false },
            ),
          );
        }

        this.codeEtape.markAsTouched();

        const certification = étape.certification;
        const referentiel = this.referentiel();
        if (certification && referentiel) {
          this.#certificationStore.parCodeFiche({ codeFiche: certification.codeFiche, idReferentiel: referentiel.id });
        } else {
          this.#certificationStore.unselect();
        }

        const peutModifierGénéral =
          étape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS &&
          étape.estOuverte &&
          !étape.estSupprimee &&
          !étape.estExportee &&
          this.#canEditService.check(
            this.referentiel()?.equipeResponsable,
            ['PARCOURS'],
            LivrablePrincipal.PARCOURS_CROISEMENT,
            this.cartographie()!,
            this.referentiel(),
          );
        if (peutModifierGénéral) {
          this.form.enable({ emitEvent: false });
        } else {
          this.form.disable({ emitEvent: false });
        }

        if (étape.estDupliquee) {
          this.contrat.disable({ emitEvent: false });
        }

        if (showErrors) this.form.markAllAsTouched();
      }
    });
  }

  submitÉtape() {
    const etape = this.#etapeSelectionnee();
    if (!etape) return;

    this.#sidepanelService.loading.set(true);
    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: this.nom.value,
            codeEtape: this.codeEtape.value,
            duree: this.duree.value,
            typesContrat: this.#getAllTypesContrat(),
            idModalite: this.modalite.value,
            idCertification: this.certification.value?.id ?? null,
            aChangementVille: this.aChangementVille.value,
            aCursusInternational: this.aCursusInternational.value,
            estCertifiante: this.estCertifiante.value,
            formationsReglementaires: (this.nomFormationReglementaires.value as { nom: string }[])?.map((x) => x.nom),
            idsAptitudes: (this.aptitudes?.value as Aptitude[])?.filter((x) => !!x).map((x) => x.id),
            idsFamillesLivrables: (this.famillesLivrables?.value as FamilleLivrables[])
              ?.filter((x) => !!x)
              .map((x) => x.id),
            idsProductions: this.#getAllIdsProduction(),
            aptitudesInterrelations: this.#getAllIdsInterrelation(),
            estOuverte: this.estOuverte.value,
            repartitionsAnnee: this.#getRepartitions(),
            idsGroupesEtapesALaCarte: this.groupes.value,
            type: this.certification.value !== null ? null : this.type.value,
            etatValidation: etape.etatValidation,
          },
        ],
      },
      next: () => {
        this.#etapeStore.getEtape(etape.id);

        if (this.groupes.value?.length) {
          etape.idsGroupesEtapesALaCarte?.forEach((id) => {
            if (this.groupes.value.includes(id)) return;

            const groupeÉtapes = this.#groupeEtapeStore.entityMap()[id];
            if (!groupeÉtapes) throw Error();

            const index = groupeÉtapes.etapes.findIndex((x) => x.id === etape.id);
            groupeÉtapes.etapes.splice(index, 1);
          });

          this.groupes.value.forEach((id) => {
            const groupeÉtapes = this.#groupeEtapeStore.entityMap()[id];
            if (!groupeÉtapes) throw Error();

            if (!groupeÉtapes.etapes.map((x) => x.id).includes(etape.id)) groupeÉtapes.etapes.push(etape);
          });
        }

        this.#sidepanelService.loading.set(false);
      },
    });
  }

  ajouterFamilleLivrables() {
    const fc = this.#fb.nonNullable.control<FamilleLivrables | undefined>(undefined);
    this.famillesLivrables.push(fc, {
      emitEvent: false,
    });
  }

  ajouterAptitude(aptitude: Aptitude) {
    const fc = this.#fb.nonNullable.control<Aptitude>(aptitude);
    this.aptitudes.push(fc, {
      emitEvent: false,
    });
  }

  #getAllIdsInterrelation(): ÉtapeInterrelation[] {
    const idsForm =
      this.interrelations?.value
        .map((x) => x.id.split('-', 2))
        .map(([idAptitude, idInterrelation]) => ({ idAptitude, idInterrelation })) ?? [];
    const idsCertif = this.aptitudesCertif().flatMap((x) =>
      x.interrelations.map((interrelation) => ({ idInterrelation: interrelation.id, idAptitude: x.id })),
    );
    return [...idsForm, ...idsCertif];
  }

  #getAllIdsProduction() {
    const idsForm = (this.productions?.value as Production[])?.map((x) => x.id);
    const idsCertif = this.aptitudesCertif()
      .flatMap((x) => x.productions!)
      .map((x) => x.id);
    return [...idsForm, ...idsCertif];
  }

  #getAllTypesContrat() {
    const typesContrat = [...this.typesContrat.value.filter((x) => this.defaultTypesContrat.includes(x))];
    if (!!this.autreType.value) {
      typesContrat.push(this.autreType.value);
    }
    return typesContrat;
  }

  #getRepartitions() {
    return this.repartitionsAnnee.getRawValue().map((x) => ({
      nombreMois: x.nombreMois ?? 0,
      nombreSemainesCFA: x.nombreSemainesCFA ?? 0,
      nombreSemainesEntreprise: x.nombreSemainesEntreprise ?? 0,
      numeroAnnee: x.numeroAnnee,
    }));
  }

  get general(): FormGroup {
    return this.form.get(FormGroupNames.GENERAL) as FormGroup;
  }
  get contrat(): FormGroup {
    return this.form.get(FormGroupNames.CONTRAT) as FormGroup;
  }
  get certifications(): FormGroup {
    return this.form.get(FormGroupNames.CERTIFICATIONS) as FormGroup;
  }
  get formationsReglementaires(): FormGroup {
    return this.form.get(FormGroupNames.FORMATIONS_REGLEMENTAIRES) as FormGroup;
  }
  get objectifs(): FormGroup {
    return this.form.get(FormGroupNames.OBJECTIFS) as FormGroup;
  }
  get livrables(): FormGroup {
    return this.form.get(FormGroupNames.LIVRABLES) as FormGroup;
  }

  get nom() {
    return this.general.get(FormControlNames.NOM) as FormControl<string>;
  }
  get codeEtape() {
    return this.general.get(FormControlNames.CODE) as FormControl<string>;
  }
  get ancienCodeEtape() {
    return this.general.get(FormControlNames.ANCIEN_CODE) as FormControl<string>;
  }

  get estOuverte() {
    return this.general.get(FormControlNames.EST_OUVERTE) as FormControl<boolean>;
  }

  get estALaCarte() {
    return this.general.get(FormControlNames.EST_À_LA_CARTE) as FormControl<boolean>;
  }

  get groupes() {
    return this.general.get(FormControlNames.GROUPES) as FormControl<string[]>;
  }

  get aChangementVille() {
    return this.general.get(FormControlNames.A_CHANGEMENT_VILLE) as FormControl<boolean>;
  }

  get aCursusInternational() {
    return this.general.get(FormControlNames.A_CURSUS_INTERNATIONAL) as FormControl<boolean>;
  }

  get type() {
    return this.contrat.get(FormControlNames.TYPE) as FormControl<string | null>;
  }

  get typesContrat() {
    return this.contrat.get(FormControlNames.TYPES_CONTRAT) as FormControl<string[]>;
  }
  get autreType() {
    return this.contrat.get(FormControlNames.AUTRE_TYPE) as FormControl<string>;
  }
  get modalite() {
    return this.contrat.get(FormControlNames.MODALITE) as FormControl<string>;
  }
  get duree() {
    return this.contrat.get(FormControlNames.DUREE) as FormControl<number | null>;
  }

  get repartitionsAnnee() {
    return this.contrat.get(FormControlNames.REPARTITIONS_ANNEE) as FormArray<FormGroup<AnneeForm>>;
  }

  get estCertifiante() {
    return this.contrat.get(FormControlNames.EST_CERTIFIANTE) as FormControl<boolean>;
  }

  get aptitudes() {
    return this.objectifs.get(FormControlNames.APTITUDES) as FormArray<FormControl>;
  }
  get interrelations() {
    return this.objectifs.get(FormControlNames.INTERRELATIONS) as FormControl<{ id: string }[]>;
  }
  get productions() {
    return this.objectifs.get(FormControlNames.PRODUCTIONS) as FormControl<Production[]>;
  }

  get certification() {
    return this.certifications.get(FormControlNames.CERTIFICATION) as FormControl<Certification | null>;
  }

  get nomFormationReglementaires() {
    return this.formationsReglementaires.get(FormControlNames.NOM_FORMATIONS_REGLEMENTAIRES) as FormControl<
      { nom: string }[]
    >;
  }

  get famillesLivrables() {
    return this.livrables.get(FormControlNames.FAMILLES_LIVRABLES) as FormArray<FormControl>;
  }
}

export interface AnneeForm {
  nombreMois: FormControl<number | null>;
  nombreSemainesCFA: FormControl<number | null>;
  nombreSemainesEntreprise: FormControl<number | null>;
  numeroAnnee: FormControl<1 | 2 | 3>;
}
